import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import {
    AbilityLookup,
    Action,
    ActionUtils,
    rulesEngineSelectors,
    characterActions,
    DataOriginRefData,
    Feat,
    FeatUtils,
    RuleData,
    SnippetData,
    Spell,
    SpellUtils,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';

import { FeatFeatureSnippet } from '../../../../CharacterSheet/components/FeatureSnippet';
import { sidebarActions } from '../../../actions/sidebar';
import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader } from '../../../components/Sidebar';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum, PaneIdentifierUtils } from '../../../utils';
import { PaneIdentifiersFeat } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    feats: Array<Feat>;
    identifiers: PaneIdentifiersFeat | null;

    snippetData: SnippetData;
    ruleData: RuleData;
    abilityLookup: AbilityLookup;
    dataOriginRefData: DataOriginRefData;
    isReadonly: boolean;
    proficiencyBonus: number;
    theme: CharacterTheme;
}
interface State {
    feat: Feat | null;
}
class FeatPane extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = this.generateStateData(props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { feats, identifiers } = this.props;

        if (feats !== prevProps.feats || identifiers !== prevProps.identifiers) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { feats, identifiers } = props;

        let foundFeat: Feat | null | undefined = null;
        if (identifiers) {
            foundFeat = feats.find((feat) => identifiers.id === FeatUtils.getId(feat));
        }
        return {
            feat: foundFeat ? foundFeat : null,
        };
    };

    handleActionUseSet = (action: Action, uses: number): void => {
        const { dispatch } = this.props;

        const id = ActionUtils.getId(action);
        const entityTypeId = ActionUtils.getEntityTypeId(action);
        if (id !== null && entityTypeId !== null) {
            dispatch(characterActions.actionUseSet(id, entityTypeId, uses, ActionUtils.getDataOriginType(action)));
        }
    };

    handleSpellUseSet = (spell: Spell, uses: number): void => {
        const { dispatch } = this.props;

        const mappingId = SpellUtils.getMappingId(spell);
        const mappingEntityTypeId = SpellUtils.getMappingEntityTypeId(spell);

        if (mappingId && mappingEntityTypeId) {
            dispatch(
                characterActions.spellUseSet(mappingId, mappingEntityTypeId, uses, SpellUtils.getDataOriginType(spell)),
            );
        }
    };

    handleSpellDetailShow = (spell: Spell): void => {
        const { dispatch } = this.props;

        const mappingId = SpellUtils.getMappingId(spell);
        if (mappingId !== null) {
            dispatch(
                sidebarActions.paneHistoryPush(
                    PaneComponentEnum.CHARACTER_SPELL_DETAIL,
                    PaneIdentifierUtils.generateCharacterSpell(mappingId),
                ),
            );
        }
    };

    handleFeatShow = (feat: Feat): void => {
        const { dispatch } = this.props;
        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.FEAT_DETAIL,
                PaneIdentifierUtils.generateFeat(FeatUtils.getId(feat)),
            ),
        );
    };

    handleActionShow = (action: Action): void => {
        const { dispatch } = this.props;

        const mappingId = ActionUtils.getMappingId(action);
        const mappingEntityTypeId = ActionUtils.getMappingEntityTypeId(action);
        if (mappingId !== null && mappingEntityTypeId !== null) {
            dispatch(
                sidebarActions.paneHistoryPush(
                    PaneComponentEnum.ACTION,
                    PaneIdentifierUtils.generateAction(mappingId, mappingEntityTypeId),
                ),
            );
        }
    };

    render() {
        const { feat } = this.state;

        const { abilityLookup, snippetData, ruleData, isReadonly, feats, dataOriginRefData, proficiencyBonus, theme } =
            this.props;

        if (feat === null) {
            return <PaneInitFailureContent />;
        }

        let prerequisiteDescription = FeatUtils.getPrerequisiteDescription(feat);

        return (
            <div className="ct-feat-pane" key={FeatUtils.getId(feat)}>
                <SidebarHeader>{FeatUtils.getName(feat)}</SidebarHeader>
                {prerequisiteDescription && (
                    <div className="ct-feat-pane__prerequisite">Prerequisite: {prerequisiteDescription}</div>
                )}
                <FeatFeatureSnippet
                    feat={feat}
                    feats={feats}
                    onActionUseSet={this.handleActionUseSet}
                    onActionClick={this.handleActionShow}
                    onSpellUseSet={this.handleSpellUseSet}
                    onSpellClick={this.handleSpellDetailShow}
                    onFeatureClick={this.handleFeatShow}
                    showHeader={false}
                    showDescription={true}
                    snippetData={snippetData}
                    ruleData={ruleData}
                    abilityLookup={abilityLookup}
                    dataOriginRefData={dataOriginRefData}
                    isReadonly={isReadonly}
                    proficiencyBonus={proficiencyBonus}
                    theme={theme}
                />
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        feats: rulesEngineSelectors.getFeats(state),
        snippetData: rulesEngineSelectors.getSnippetData(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        abilityLookup: rulesEngineSelectors.getAbilityLookup(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
    };
}

export default connect(mapStateToProps)(FeatPane);
